import axios from 'axios';
import { GETREQUESTOPTIONS } from './apiService';

const API_URL = '/api/reports';

export type CaseDocumentStatus = 'draft' | 'completed' | 'final' | 'archived';
export type CaseStatus =
    | 'draft'
    | 'auto-saved'
    | 'completed'
    | 'final'
    | 'archived';

export interface CaseDocumentSummary {
    document_id: string;
    schema_id: string;
    schemaKey: string;
    schemaVersion: number;
    title: string;
    status: CaseDocumentStatus;
    data: Record<string, any>;
    document?: {
        _id: string;
        schema_id: string;
        schemaKey: string;
        schemaVersion: number;
        title: string;
        status: CaseDocumentStatus;
        data: Record<string, any>;
        schema: Record<string, any>;
    };
}

export interface EditLogEntry {
    action: string;
    userName: string;
    comment?: string;
    date: string;
}

export interface CaseReport {
    _id: string;
    department_id: string;
    caseId: string;
    status?: CaseStatus;
    needsMySignature?: boolean;
    cptEligibleCodes: {
        '76014': number;
        '76015': number;
        '76016': number;
        '76018': number;
        '76019': number;
    };
    documents: CaseDocumentSummary[];
    editLog?: EditLogEntry[];
    createdAt: string;
    updatedAt: string;
}

export interface PaginatedResult<T> {
    data: T[];
    total: number;
    page: number;
    limit: number;
}

export type ReportSortValue =
    | 'scanDate:desc'
    | 'scanDate:asc'
    | 'updatedAt:desc'
    | 'updatedAt:asc'
    | 'createdAt:desc'
    | 'createdAt:asc';

export const REPORT_SORT_OPTIONS: { value: ReportSortValue; label: string }[] =
    [
        { value: 'updatedAt:desc', label: 'Last updated (newest)' },
        { value: 'updatedAt:asc', label: 'Last updated (oldest)' },
        { value: 'scanDate:desc', label: 'Scan date (newest)' },
        { value: 'scanDate:asc', label: 'Scan date (oldest)' },
        { value: 'createdAt:desc', label: 'Created (newest)' },
        { value: 'createdAt:asc', label: 'Created (oldest)' },
    ];

export const DEFAULT_REPORT_SORT: ReportSortValue = 'updatedAt:desc';

export type ReportFilterKey = 'cptCode' | 'status' | 'needsMySignature';

export const REPORT_FILTER_KEYS: ReportFilterKey[] = [
    'cptCode',
    'status',
    'needsMySignature',
];

export type ReportFilters = Partial<Record<ReportFilterKey, string[]>>;

export interface GetMyReportsParams {
    page?: number;
    limit?: number;
    search?: string;
    sort?: ReportSortValue;
    filters?: ReportFilters;
}

export interface GenerateReportFieldPayload {
    instruction: string;
    values: Record<string, string>;
}

export interface GenerateReportFieldResponse {
    value: string;
}

export const saveReport = async (
    data: Record<string, any>,
    caseRefId?: string,
    title?: string,
    status: CaseStatus = 'draft',
    schemaKey: string = 'initial-evaluation',
    schemaVersion?: number,
    comment?: string
): Promise<CaseReport> => {
    const payload = {
        data,
        caseId: caseRefId,
        reportId: caseRefId,
        title,
        status,
        schemaKey,
        schemaVersion,
        ...(comment ? { comment } : {}),
    };

    const response = await axios.post(
        `${API_URL}/save`,
        payload,
        GETREQUESTOPTIONS()
    );
    return response.data;
};

export const saveReportDraft = async (
    data: Record<string, any>,
    caseRefId?: string,
    title?: string,
    schemaKey: string = 'initial-evaluation',
    schemaVersion?: number
): Promise<CaseReport> => {
    const payload = {
        data,
        caseId: caseRefId,
        reportId: caseRefId,
        title,
        schemaKey,
        schemaVersion,
    };
    const response = await axios.post(
        `${API_URL}/draft`,
        payload,
        GETREQUESTOPTIONS()
    );
    return response.data;
};

export const getReport = async (caseRefId: string): Promise<CaseReport> => {
    const response = await axios.get(
        `${API_URL}/${caseRefId}`,
        GETREQUESTOPTIONS()
    );
    return response.data;
};

export const getMyReports = async (
    params?: GetMyReportsParams
): Promise<PaginatedResult<CaseReport>> => {
    const { filters, ...rest } = params ?? {};
    const queryParams: Record<string, any> = { ...rest };

    if (filters) {
        for (const [key, values] of Object.entries(filters) as [
            ReportFilterKey,
            string[] | undefined
        ][]) {
            if (!values || values.length === 0) continue;
            queryParams[`filter[${key}]`] = values.join(',');
        }
    }

    const response = await axios.get(`${API_URL}/my-reports`, {
        ...GETREQUESTOPTIONS(),
        params: queryParams,
    });
    return response.data;
};

export const getReportingFormData = async (
    implantId?: string,
    activeConfigId?: string,
    activeSystemId?: string,
    activeConfigIds?: string
): Promise<any> => {
    let url = `${API_URL}/form-data`;
    const params = new URLSearchParams();
    if (implantId) params.append('implantId', implantId);
    if (activeConfigId) params.append('activeConfigId', activeConfigId);
    if (activeSystemId) params.append('activeSystemId', activeSystemId);
    if (activeConfigIds) params.append('activeConfigIds', activeConfigIds);
    if (params.toString()) url += `?${params.toString()}`;
    const response = await axios.get(url, GETREQUESTOPTIONS());
    return response.data;
};

export const deleteReport = async (
    caseRefId: string
): Promise<{ success: boolean; message: string }> => {
    const response = await axios.delete(
        `${API_URL}/${caseRefId}`,
        GETREQUESTOPTIONS()
    );
    return response.data;
};

export const generateReportField = async (
    payload: GenerateReportFieldPayload
): Promise<GenerateReportFieldResponse> => {
    const response = await axios.post(
        `${API_URL}/generate-field`,
        payload,
        GETREQUESTOPTIONS()
    );
    return response.data;
};
